export const DOC_TYPES = ["product_knowledge", "pricing", "policy", "process", "sales_script", "training", "app_guide"] as const;

export type KbAudience = "customer" | "internal" | "both" | "any";
export type KbScope = "app" | "manufacturer" | "org";
export type KbStatus = "draft" | "published";
export type KbDocType = (typeof DOC_TYPES)[number];

export type SearchResult = {
  id: string;
  slug: string;
  title: string;
  audience: string;
  scope?: string | null;
  doc_type?: string | null;
  locations: string[];
  tags: string[];
  status: string;
  published_at: string | null;
  last_edited_by: string | null;
  organization_id?: string | null;
  visibility?: string | null;
  chunk_count?: number;
  total_chunk_chars?: number;
  avg_chunk_chars?: number;
  risk?: "low" | "moderate" | "healthy";
};

export type BulkImportItem = {
  slug?: string;
  title: string;
  content?: string;
  body?: string;
  answerHint?: string | null;
  chunks?: { index?: number; text?: string }[];
  audience?: KbAudience;
  scope?: KbScope;
  doc_type?: string | null;
  tags?: string[];
  locations?: string[];
  sourceUrl?: string | null;
  source_url?: string | null;
  status?: KbStatus;
};

export type PanelNote = {
  tone: "info" | "success" | "error";
  title: string;
  text: string;
};

export type GapInboxItem = {
  id: string;
  organization_id: string | null;
  title: string;
  summary: string;
  priority: "low" | "medium" | "high";
  article_draft: string;
  linked_article_slugs: { slug: string; reason: string; action: "revise" | "reference" }[];
  source_article_slug: string | null;
  item_type: "gap" | "link_review" | "query_miss";
  status: "open" | "accepted" | "dismissed" | "in_progress" | "done";
  submitted_by: string | null;
  created_at: string;
  updated_at: string;
};

export type GapSolutionSuggestion = {
  id: string;
  slug: string;
  title: string;
  scope: string | null;
  doc_type: string | null;
  status: string;
  tags: string[];
  updated_at: string | null;
  last_edited_by: string | null;
  score: number;
  confidence: number;
  reason: string;
  suggested_action: "revise" | "reference";
};

export type GapSolutionResponse = {
  ok: true;
  suggestions: GapSolutionSuggestion[];
  concise_title_suggestion: string;
  missing_article_blueprint: {
    summary: string;
    doc_type_hint: string;
    required_terms: string[];
  };
  ai_used: boolean;
};

export type AiHelpResponse = {
  suggestion: {
    title: string;
    audience: KbAudience;
    scope: KbScope;
    doc_type: string | null;
    status: KbStatus;
    tags: string[];
    notes: string[];
    resolved_facts: string[];
    follow_up_questions: string[];
    suggested_action: "create_new" | "revise_existing" | "reference_existing";
    placement_summary: string;
    topic_complexity: "simple" | "moderate" | "broad";
    recommended_doc_count: number;
    stop_reason: "enough_information" | "user_requested_stop" | "needs_split" | "missing_critical_fact";
    follow_on_docs: {
      title: string;
      reason: string;
      source_question: string;
      linked_articles: {
        slug: string;
        reason: string;
        action: "revise" | "reference";
      }[];
    }[];
    kb_gap_candidates: {
      title: string;
      summary: string;
      priority: "low" | "medium" | "high";
      linked_articles: {
        slug: string;
        reason: string;
        action: "revise" | "reference";
      }[];
      article_draft: string;
    }[];
    article_link_suggestions: {
      slug: string;
      title: string;
      relationship: "prerequisite" | "related_context" | "see_also" | "update_existing";
      reason: string;
      action: "revise" | "reference";
    }[];
    body_draft: string;
    body_generation_notes: string[];
    related_articles: {
      id: string;
      slug: string;
      title: string;
      scope: string | null;
      doc_type: string | null;
      status: string;
      last_edited_by: string | null;
      reason: string;
      action: "revise" | "reference";
    }[];
  };
  access: {
    allowedScopes: string[];
    canPublish: boolean;
    activeOrganizationId: string | null;
  };
};

export type AppliedSuggestion = AiHelpResponse["suggestion"] & {
  accessSummary?: string;
};

export type AiQuestionAnswer = {
  question: string;
  answer: string;
  disposition: "open" | "answered" | "not_applicable" | "different_document";
};

export type RelatedArticlePreview = {
  slug: string;
  title: string;
  body_markdown: string;
  scope: string | null;
  doc_type: string | null;
  status: string;
  last_edited_by: string | null;
  updated_at: string | null;
};

export type KbSplitProposalItem = {
  title: string;
  body: string;
  doc_type: string;
  reason: string;
  checked: boolean;
  created: boolean;
};

export type TrashResult = {
  id: string;
  slug: string;
  title: string;
  scope: string | null;
  doc_type: string | null;
  last_edited_by: string | null;
  deleted_at: string | null;
};